import { useQuery } from "@tanstack/react-query";
import { useState, useMemo } from "react";
import { ExternalLink, Newspaper, Star, Filter, X } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { FloatingWhatsApp } from "@/components/FloatingWhatsApp";
import { AccessibilityButton } from "@/components/AccessibilityButton";
import { usePageVisibility, PAGE_VISIBILITY_KEYS } from "@/hooks/usePageVisibility";
import { PageUnavailable } from "@/components/ui/PageUnavailable";
import { pressMentionsApi, PressMention } from "@/integrations/mysql/client";

// ── Helpers ────────────────────────────────────────────────────────────────────

function formatDate(dateStr: string) {
  if (!dateStr) return "";
  try {
    const [year, month, day] = dateStr.split("T")[0].split("-").map(Number);
    return new Date(year, month - 1, day).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      timeZone: "America/Sao_Paulo",
    });
  } catch {
    return dateStr;
  }
}

function getYear(dateStr: string | null): string | null {
  if (!dateStr) return null;
  return dateStr.split("T")[0].split("-")[0];
}

const CATEGORY_COLORS: Record<string, string> = {
  TV:        "bg-red-50 text-red-700",
  Rádio:     "bg-orange-50 text-orange-700",
  Podcast:   "bg-purple-50 text-purple-700",
  Jornal:    "bg-blue-50 text-blue-700",
  Revista:   "bg-pink-50 text-pink-700",
  Online:    "bg-teal-50 text-teal-700",
  Instagram: "bg-violet-50 text-violet-700",
  Outros:    "bg-gray-100 text-gray-600",
};

function categoryColor(cat: string) {
  return CATEGORY_COLORS[cat] ?? "bg-gray-100 text-gray-600";
}

// ── Logo bubble ────────────────────────────────────────────────────────────────

function Logo({ mention, size = "md" }: { mention: PressMention; size?: "sm" | "md" | "lg" }) {
  const dim = size === "lg" ? "w-16 h-16" : size === "md" ? "w-12 h-12" : "w-9 h-9";
  const text = size === "lg" ? "text-xl" : size === "md" ? "text-base" : "text-sm";

  if (mention.publication_logo_url) {
    return (
      <img
        src={mention.publication_logo_url}
        alt={mention.publication_name}
        className={`${dim} rounded-xl object-contain bg-white border border-gray-100 p-1 shrink-0`}
      />
    );
  }
  const initials = mention.publication_name
    .split(" ")
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase();
  return (
    <div className={`${dim} rounded-xl bg-brand-primary/10 flex items-center justify-center shrink-0`}>
      <span className={`font-bold text-brand-primary ${text}`}>{initials}</span>
    </div>
  );
}

// ── Featured card ──────────────────────────────────────────────────────────────

function FeaturedCard({ mention }: { mention: PressMention }) {
  return (
    <a
      href={mention.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group block bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
    >
      {mention.cover_image_url && (
        <div className="w-full h-52 overflow-hidden">
          <img
            src={mention.cover_image_url}
            alt={mention.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <div className="flex flex-col sm:flex-row">
        {/* Accent strip */}
        <div className={`sm:w-2 h-2 sm:h-auto bg-brand-primary ${mention.cover_image_url ? "" : "rounded-t-2xl"} sm:rounded-l-2xl sm:rounded-tr-none shrink-0`} />

        <div className="flex flex-col sm:flex-row gap-6 p-6 sm:p-8 flex-1">
          <Logo mention={mention} size="lg" />

          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="inline-flex items-center gap-1 bg-brand-primary/10 text-brand-primary text-xs font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
                <Star className="w-3 h-3 fill-current" />
                Destaque
              </span>
              {mention.category && (
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${categoryColor(mention.category)}`}>
                  {mention.category}
                </span>
              )}
              {mention.subject && mention.subject.split(",").map(s => s.trim()).filter(Boolean).map(tag => (
                <span key={tag} className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600">
                  {tag}
                </span>
              ))}
              {mention.published_at && (
                <span className="text-xs text-gray-400">{formatDate(mention.published_at)}</span>
              )}
            </div>

            <p className="text-xs font-semibold text-brand-primary uppercase tracking-wider mb-1">
              {mention.publication_name}
            </p>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-800 font-primary group-hover:text-brand-primary transition-colors leading-snug mb-3">
              {mention.title}
            </h2>
            {mention.excerpt && (
              <p className="text-gray-500 text-sm leading-relaxed line-clamp-3">{mention.excerpt}</p>
            )}

            <div className="mt-4 inline-flex items-center gap-1.5 text-brand-primary text-sm font-semibold group-hover:gap-2.5 transition-all">
              Ler artigo
              <ExternalLink className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>
      </div>
    </a>
  );
}

// ── Mention card ───────────────────────────────────────────────────────────────

function MentionCard({ mention }: { mention: PressMention }) {
  return (
    <a
      href={mention.url}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md hover:border-brand-primary/20 transition-all duration-200 overflow-hidden"
    >
      {mention.cover_image_url && (
        <div className="w-full aspect-[4/3] overflow-hidden">
          <img
            src={mention.cover_image_url}
            alt={mention.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        </div>
      )}
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start gap-3 mb-4">
          <Logo mention={mention} size="sm" />
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold text-gray-500 truncate">{mention.publication_name}</p>
            {mention.published_at && (
              <p className="text-xs text-gray-400 mt-0.5">{formatDate(mention.published_at)}</p>
            )}
          </div>
          {mention.category && (
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full shrink-0 ${categoryColor(mention.category)}`}>
              {mention.category}
            </span>
          )}
        </div>

        <h3 className="text-sm font-bold text-gray-800 font-primary group-hover:text-brand-primary transition-colors leading-snug mb-2 line-clamp-3">
          {mention.title}
        </h3>

        {mention.subject && (
          <div className="flex flex-wrap gap-1 mb-2">
            {mention.subject.split(",").map(s => s.trim()).filter(Boolean).map(tag => (
              <span key={tag} className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">
                {tag}
              </span>
            ))}
          </div>
        )}

        {mention.excerpt && (
          <p className="text-xs text-gray-400 leading-relaxed line-clamp-3 flex-1">{mention.excerpt}</p>
        )}
      </div>

      <div className="px-5 pb-4">
        <div className="inline-flex items-center gap-1 text-brand-primary text-xs font-semibold group-hover:gap-2 transition-all">
          Ler artigo
          <ExternalLink className="w-3 h-3" />
        </div>
      </div>
    </a>
  );
}

// ── Filter bar ─────────────────────────────────────────────────────────────────

interface Filters {
  year: string;
  vehicleType: string;
  subject: string;
}

function FilterBar({
  mentions,
  filters,
  onChange,
}: {
  mentions: PressMention[];
  filters: Filters;
  onChange: (f: Filters) => void;
}) {
  const years = useMemo(() => {
    const set = new Set<string>();
    mentions.forEach(m => { const y = getYear(m.published_at); if (y) set.add(y); });
    return Array.from(set).sort((a, b) => Number(b) - Number(a));
  }, [mentions]);

  const vehicleTypes = useMemo(() => {
    const set = new Set<string>();
    mentions.forEach(m => { if (m.category) set.add(m.category); });
    return Array.from(set).sort();
  }, [mentions]);

  const subjects = useMemo(() => {
    const set = new Set<string>();
    mentions.forEach(m => {
      if (m.subject) m.subject.split(",").map(s => s.trim()).filter(Boolean).forEach(t => set.add(t));
    });
    return Array.from(set).sort();
  }, [mentions]);

  const hasFilter = filters.year || filters.vehicleType || filters.subject;

  const selectCls = "text-sm border border-gray-200 rounded-xl px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-all cursor-pointer";

  if (years.length === 0 && vehicleTypes.length === 0 && subjects.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 mb-8 bg-white border border-gray-100 rounded-2xl p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wide shrink-0">
        <Filter className="w-3.5 h-3.5" />
        Filtrar
      </div>

      {years.length > 0 && (
        <select value={filters.year} onChange={e => onChange({ ...filters, year: e.target.value })} className={selectCls}>
          <option value="">Todos os anos</option>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      )}

      {vehicleTypes.length > 0 && (
        <select value={filters.vehicleType} onChange={e => onChange({ ...filters, vehicleType: e.target.value })} className={selectCls}>
          <option value="">Tipo de veículo</option>
          {vehicleTypes.map(v => <option key={v} value={v}>{v}</option>)}
        </select>
      )}

      {subjects.length > 0 && (
        <select value={filters.subject} onChange={e => onChange({ ...filters, subject: e.target.value })} className={selectCls}>
          <option value="">Assunto</option>
          {subjects.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      )}

      {hasFilter && (
        <button
          onClick={() => onChange({ year: "", vehicleType: "", subject: "" })}
          className="flex items-center gap-1 text-xs font-medium text-gray-400 hover:text-red-500 transition-colors ml-auto"
        >
          <X className="w-3.5 h-3.5" />
          Limpar filtros
        </button>
      )}
    </div>
  );
}

// ── Skeleton ───────────────────────────────────────────────────────────────────

function Skeleton({ className }: { className?: string }) {
  return <div className={`animate-pulse bg-gray-100 rounded-xl ${className}`} />;
}

function LoadingGrid() {
  return (
    <div className="space-y-8">
      <Skeleton className="h-44 w-full rounded-2xl" />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {Array.from({ length: 6 }).map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-100 p-5 space-y-3">
            <div className="flex items-start gap-3">
              <Skeleton className="w-9 h-9 shrink-0" />
              <div className="flex-1 space-y-1.5">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-2.5 w-16" />
              </div>
            </div>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-5/6" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function Imprensa() {
  const { isVisible } = usePageVisibility();
  if (!isVisible(PAGE_VISIBILITY_KEYS.imprensa)) return <PageUnavailable />;

  const { data: mentions = [], isLoading } = useQuery({
    queryKey: ["press-mentions-public"],
    queryFn: () => pressMentionsApi.list(true),
  });

  const [filters, setFilters] = useState<Filters>({ year: "", vehicleType: "", subject: "" });

  const filtered = useMemo(() => {
    return mentions.filter(m => {
      if (filters.year && getYear(m.published_at) !== filters.year) return false;
      if (filters.vehicleType && m.category !== filters.vehicleType) return false;
      if (filters.subject) {
        const tags = m.subject ? m.subject.split(",").map(s => s.trim()) : [];
        if (!tags.includes(filters.subject)) return false;
      }
      return true;
    });
  }, [mentions, filters]);

  const featured = filtered.find((m) => m.is_featured);
  const regular = filtered.filter((m) => !m.is_featured);
  const hasFilter = !!(filters.year || filters.vehicleType || filters.subject);

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Hero */}
      <section className="pt-24 bg-brand-primary py-14">
        <div className="container mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white/15 text-white/90 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase">
            <Newspaper className="w-3.5 h-3.5" />
            Imprensa
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-white font-primary mb-3">
            Na Mídia
          </h1>
          <p className="text-white/70 text-base max-w-xl mx-auto">
            Acompanhe as aparições da Dra. Brenda Ramos em veículos de comunicação, podcasts, revistas e portais de saúde.
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-14">
        <div className="container mx-auto px-6 max-w-6xl">
          {isLoading ? (
            <LoadingGrid />
          ) : mentions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center mb-4">
                <Newspaper className="w-7 h-7 text-gray-300" />
              </div>
              <p className="text-gray-500 font-medium">Nenhuma publicação disponível ainda.</p>
              <p className="text-gray-400 text-sm mt-1">Volte em breve para acompanhar as novidades.</p>
            </div>
          ) : (
            <>
              <FilterBar mentions={mentions} filters={filters} onChange={setFilters} />

              {filtered.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center mb-3">
                    <Filter className="w-6 h-6 text-gray-300" />
                  </div>
                  <p className="text-gray-500 font-medium">Nenhuma publicação encontrada com estes filtros.</p>
                  <button
                    onClick={() => setFilters({ year: "", vehicleType: "", subject: "" })}
                    className="mt-3 text-brand-primary text-sm font-semibold hover:underline"
                  >
                    Limpar filtros
                  </button>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Featured — only show when not filtered or featured passes filter */}
                  {featured && (
                    <div>
                      <FeaturedCard mention={featured} />
                    </div>
                  )}

                  {/* Grid */}
                  {regular.length > 0 && (
                    <div>
                      {(featured || hasFilter) && (
                        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest mb-5">
                          {hasFilter ? `${filtered.length} publicaç${filtered.length === 1 ? "ão" : "ões"} encontrada${filtered.length === 1 ? "" : "s"}` : "Todas as aparições"}
                        </h2>
                      )}
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
                        {regular.map((mention) => (
                          <MentionCard key={mention.id} mention={mention} />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
      <AccessibilityButton />
    </div>
  );
}
